import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { AlbumDraftChoices, AlbumView, ItineraryDay, JournalEntry, TripPhoto } from '../../types';
import { albumApi } from '../../api/album.api';
import { toast } from '../ui/Toast';
import LoadingScreen from '../ui/LoadingScreen';
import EmptyState from '../ui/EmptyState';
import { BookImage } from '../ui/Icon';
import AlbumEditor from './AlbumEditor';
import AlbumWizard from './AlbumWizard';
import { albumMapKeys } from '../../utils/albumMaps';
import { pagesOf } from '../../utils/albumSpreads';
import { ensureAlbumFonts } from '../../utils/albumFonts';

interface Props {
  tripId: string;
  photos: TripPhoto[];
  entries: JournalEntry[];
  days: ItineraryDay[];
}

/** The Album view of the Diary: start one from what was lived, or keep editing yours. */
export default function AlbumSection({ tripId, photos, entries, days }: Props) {
  const { t } = useTranslation();
  const [view, setView] = useState<AlbumView | null>(null);
  const [wizard, setWizard] = useState<'create' | 'restart' | null>(null);

  useEffect(() => {
    albumApi.get(tripId).then(setView).catch(() => toast(t('album.loadError'), 'error'));
  }, [tripId]);

  // Writes return the album, not its maps: a map page that's new (or a day
  // just chosen) re-reads the view once to get its drawing (ADR-0047).
  useEffect(() => { if (view) ensureAlbumFonts(view.styles.fonts); }, [view]);

  const askedFor = useRef(new Set<string>());
  useEffect(() => {
    if (!view?.album) return;
    const missing = albumMapKeys(pagesOf(view.album)).filter(key => !(key in view.maps) && !askedFor.current.has(key));
    if (missing.length === 0) return;
    missing.forEach(key => askedFor.current.add(key));
    albumApi.get(tripId).then(setView).catch(() => {});
  }, [view, tripId]);

  /** ADR-0050: the wizard's choices make the draft, for a new album or over the one there is. */
  async function submitWizard(choices: AlbumDraftChoices) {
    try {
      if (wizard === 'restart') await albumApi.restart(tripId, choices);
      else await albumApi.create(tripId, choices);
      // Re-read the view: the figures page needs the summary, and the format its catalogue.
      setView(await albumApi.get(tripId));
      setWizard(null);
    } catch {
      toast(t('album.saveError'), 'error');
    }
  }

  async function changeFormat(format: string) {
    try {
      await albumApi.setFormat(tripId, format);
      setView(await albumApi.get(tripId));
    } catch {
      toast(t('album.saveError'), 'error');
    }
  }

  if (!view) return <LoadingScreen />;

  const wizardModal = (
    <AlbumWizard
      open={wizard !== null}
      tripId={tripId}
      mode={wizard ?? 'create'}
      formats={view.catalogue.formats}
      styles={view.styles}
      photos={photos}
      initial={view.album
        ? { format: view.album.format, theme: view.album.theme, density: view.album.density }
        : { format: view.catalogue.format, theme: view.styles.defaultTheme, density: 'NORMAL' }}
      onClose={() => setWizard(null)}
      onSubmit={submitWizard}
    />
  );

  if (!view.album) {
    return (
      <>
        <EmptyState
          icon={BookImage}
          title={t('album.emptyTitle')}
          body={t('album.emptyBody')}
          action={{ label: t('album.create'), icon: BookImage, onClick: () => setWizard('create') }}
        />
        {wizardModal}
      </>
    );
  }

  return (
    <>
    {wizardModal}
    <AlbumEditor
      key={view.album.id}
      tripId={tripId}
      album={view.album}
      catalogue={view.catalogue}
      summary={view.summary}
      maps={view.maps}
      trip={view.trip}
      styles={view.styles}
      photos={photos}
      entries={entries}
      days={days}
      onChange={album => setView(v => v && { ...v, album })}
      onRestart={() => setWizard('restart')}
      onFormat={changeFormat}
    />
    </>
  );
}
